import { Info, Plus, Trash } from 'lucide-react';
import React, { useEffect } from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useStreamServeProjekte } from '@/adapter/opm-cockpit-backend/useStreamServeProjekte';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  DokumentTypFormInput,
  emailVersandArtOptions,
  versandartUebersteuerungOptions,
} from '@/pages/Konfiguration/DokumentTypen/schemas/dokumenttypenerstellen';

const MAX_BEILAGEN = 5;

const VERSANDART_UEBERSTEUERUNG_LABEL: Record<
  (typeof versandartUebersteuerungOptions)[number],
  string
> = {
  E_MAIL: 'E-Mail',
  BRIEF: 'Brief',
};

export const CreateMappingRulesBox: React.FC<{
  form: UseFormReturn<DokumentTypFormInput>;
}> = ({ form }) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const {
    fields: beilagenFields,
    append: appendBeilage,
    remove: removeBeilage,
  } = useFieldArray<DokumentTypFormInput, 'beilagen', 'id'>({
    control,
    name: 'beilagen',
  });

  const toolTipEmailVersandArt =
    'Mögliche Werte:\n\n' +
    'OHNE_DOKUMENT - Es wird ausschließlich eine E-Mail erzeugt.\n\n' +
    'DOKUMENT - Es wird ein PDF-Dokument erstellt. Bei Versandart E-Mail wird zusätzlich eine E-Mail erzeugt.\n\n' +
    'DOKUMENT_ANHANG - Es wird ein PDF-Dokument erstellt. Bei Versandart E-Mail wird das PDF als Anhang mit der E-Mail versendet.\n\n' +
    'DOKUMENT_ANHANG_BRIEF - Es wird ein PDF-Dokument erstellt. Bei Versandart E-Mail wird das PDF als Anhang versendet. Zusätzlich wird ein Brief erstellt und postalisch versendet.\n\n' +
    'DOKUMENT_BRIEF - Es wird ein PDF-Dokument erstellt. Bei Versandart E-Mail wird zusätzlich ein Brief erstellt und postalisch versendet.';
  const prioVersand = watch('prioVersand');
  const archivierungEntkoppelnAktiv = useFeatureFlag()(
    'CORE_7492_ARCHIVIERUNG_ENTKOPPELN',
  );
  const archivierungAktiv = archivierungEntkoppelnAktiv
    ? watch('archivierungAktiv')
    : true;
  const versandartUebersteuerung = watch('versandartUebersteuerung');
  const streamserveProjektExternalId = watch('streamserveProjektExternalId');

  const { data: streamServeProjekte, isLoading: streamServeLoading } =
    useStreamServeProjekte();
  const selectedProjekt = streamServeProjekte?.find(
    (p) => p.externalId === streamserveProjektExternalId,
  );

  useEffect(() => {
    if (versandartUebersteuerung === 'BRIEF') {
      setValue('emailKundenkommunikation', null);
    }
  }, [versandartUebersteuerung, setValue]);

  return (
    <div className="bg-white border border-table-border rounded-md p-4">
      <h2 className="text-lg font-semibold text-text-primary mb-4">
        Output-Steuerung
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            StreamServe Projektname
          </span>
          <select
            {...register('streamserveProjektExternalId')}
            value={streamserveProjektExternalId ?? ''}
            disabled={streamServeLoading}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
          >
            <option value="" disabled>
              — Bitte wählen —
            </option>
            {(streamServeProjekte ?? []).map((p) => (
              <option key={p.externalId} value={p.externalId}>
                {p.projektName}
              </option>
            ))}
          </select>
          <span className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-1 min-h-4">
            {errors.streamserveProjektExternalId?.message}
          </span>
        </div>

        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Projekt-Version
          </span>
          <input
            type="text"
            readOnly
            disabled
            value={
              selectedProjekt ? String(selectedProjekt.projektVersion) : ''
            }
            placeholder="— wird automatisch gesetzt —"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-gray-100 text-gray-600"
          />
          <span className="min-h-4 inline-block" />
        </div>

        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            ExStream Servicename
          </span>
          <input
            {...register('exstreamServiceName')}
            placeholder="z.B. SERVICE_DUPLIKATVERSAND"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
          />
          <span className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-1 min-h-4">
            {errors.exstreamServiceName?.message}
          </span>
        </div>
      </div>

      <div className="pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Versandart-Übersteuerung
          </span>
          <select
            {...register('versandartUebersteuerung')}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">— Keine —</option>
            {versandartUebersteuerungOptions.map((opt) => (
              <option key={opt} value={opt}>
                {VERSANDART_UEBERSTEUERUNG_LABEL[opt]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-help">
                Output-Art
                <Info className="w-3 h-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm whitespace-pre-line text-left">
              {toolTipEmailVersandArt}
            </TooltipContent>
          </Tooltip>
          <select
            {...register('emailVersandArt')}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {emailVersandArtOptions.map((opt) => (
              <option key={opt} value={opt}>
                {[opt]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="pt-4 flex flex-wrap items-start gap-6">
        <label className="inline-flex items-center gap-2 text-sm">
          <Checkbox
            checked={!!prioVersand}
            onCheckedChange={(checked) =>
              setValue('prioVersand', checked === true, {
                shouldValidate: true,
              })
            }
          />
          Prioversand
        </label>

        {archivierungEntkoppelnAktiv && (
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox
              checked={archivierungAktiv}
              onCheckedChange={(checked) =>
                setValue('archivierungAktiv', checked === true, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            Archivierung
          </label>
        )}

        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox
                  checked={watch('emailKundenkommunikation') === true}
                  disabled={
                    !archivierungAktiv || versandartUebersteuerung === 'BRIEF'
                  }
                  onCheckedChange={(checked) =>
                    setValue('emailKundenkommunikation', checked === true, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                />
                E-Mail Kundenkommunikation
                <Info className="w-3 h-3" />
              </label>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Legt fest, ob E-Mails zusätzlich im digitalen Postfach
                (Kundenportal/EWE App) angezeigt werden.
              </p>
              {(!archivierungAktiv || versandartUebersteuerung === 'BRIEF') && (
                <p className="mt-1">
                  {!archivierungAktiv
                    ? 'Nicht bearbeitbar, weil Archivierung inaktiv ist. Zum Bearbeiten die Archivierung aktivieren.'
                    : 'Nicht bearbeitbar bei Versandart-Übersteuerung Brief.'}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {errors.archivierungAktiv && (
        <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-2">
          {errors.archivierungAktiv.message}
        </div>
      )}
      {errors.emailKundenkommunikation && (
        <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-2">
          {errors.emailKundenkommunikation.message}
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-text-primary">
            Beilagen ({beilagenFields.length}/{MAX_BEILAGEN})
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              appendBeilage({
                id: '',
                label: '',
                druck: false,
                email: false,
              })
            }
            disabled={beilagenFields.length >= MAX_BEILAGEN}
          >
            <Plus className="w-4 h-4 mr-1" /> Beilage hinzufügen
          </Button>
        </div>

        {beilagenFields.length === 0 && (
          <div className="text-sm text-text-secondary italic">
            Keine Beilagen konfiguriert.
          </div>
        )}

        {beilagenFields.map((field, index) => (
          <div
            key={field.id}
            className="grid grid-cols-[1fr_2fr_auto_auto_auto] items-end gap-3 py-2 border-b border-gray-100"
          >
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                ID
              </span>
              <input
                {...register(`beilagen.${index}.id` as const)}
                placeholder="z.B. 201"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Label
              </span>
              <input
                {...register(`beilagen.${index}.label` as const)}
                placeholder="z.B. CSS Flyer"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm pb-1">
              <input
                type="checkbox"
                {...register(`beilagen.${index}.druck` as const)}
              />
              Druck
            </label>
            <label className="inline-flex items-center gap-2 text-sm pb-1">
              <input
                type="checkbox"
                {...register(`beilagen.${index}.email` as const)}
              />
              E-Mail
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeBeilage(index)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash className="w-4 h-4" />
            </Button>
          </div>
        ))}
        {errors.beilagen?.message && (
          <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-2">
            {errors.beilagen.message}
          </div>
        )}
      </div>
    </div>
  );
};

export const mappingValuesToPayload = (values: DokumentTypFormInput) => ({
  streamserveProjektExternalId: values.streamserveProjektExternalId,
  exstreamServiceName: values.exstreamServiceName,
  versandartUebersteuerung:
    values.versandartUebersteuerung === '' ||
    values.versandartUebersteuerung == null
      ? null
      : values.versandartUebersteuerung,
  emailVersandArt: values.emailVersandArt,
  emailKundenkommunikation: values.emailKundenkommunikation,
  archivierungAktiv: values.archivierungAktiv,
  anmerkungAktiv: values.anmerkungAktiv,
  anmerkungBetreff: values.anmerkungAktiv ? values.anmerkungBetreff : null,
  anmerkungText: values.anmerkungAktiv ? values.anmerkungText : null,
  prioVersand: values.prioVersand ?? false,
  beilagen: values.beilagen,
});
